import React from "react";
import TextField from "@mui/material/TextField";
import { useCustomerForm } from "../hooks/useCustomerForm";
import { CustomerData } from "../hooks/useCustomerSearch";
import { removeEmoji } from "../../../../../utils/removeEmoji";
import { formatPincode } from "../../../../../utils/formatPincode";
import CommonButtons from "../../../../../../shared/components/CommonButtons";
import { ModalWrapper } from "../../../../../../shared/components/ModalWrapper";

interface Props {
  onBack: () => void;
  onAdded: (customer: CustomerData) => void;
}

const CustomerForm: React.FC<Props> = ({ onBack, onAdded }) => {
  const { formData, emailError, errorEmailExists, submitting, handleInputChange, handleAddCustomer, validateEmail } = useCustomerForm({
    onSuccess: onAdded,
    onBack,
  });

  // Helper to render label with red * for required fields
  const renderLabel = (label: string, required?: boolean) =>
    required ? (
      <span>
        {label} <span className='text-red-500'>*</span>
      </span>
    ) : (
      label
    );

  const fields = [
    // { key: "name", label: "Name", required: true, value: formData.name, onChange: (val: string) => handleInputChange("name", removeEmoji(val).slice(0, 25)) },
    { key: "name", label: "Name", required: true, value: formData.name, onChange: (val: string) => handleInputChange("name", val.slice(0, 25)) },
    {
      key: "phone",
      label: "Phone Number",
      required: true,
      value: formData.phone,
      onChange: (val: string) => handleInputChange("phone", val.replace(/\D/g, "").slice(0, 8)),
    },
    {
      key: "altPhone",
      label: "Alternative Phone",
      value: formData.altPhone,
      onChange: (val: string) => handleInputChange("altPhone", val.replace(/\D/g, "").slice(0, 8)),
    },
    {
      key: "email",
      label: "Email (Optional)",
      type: "email",
      value: formData.email,
      error: emailError || errorEmailExists,
      helperText: errorEmailExists ? "Email already exists" : emailError ? "Invalid email format" : "",
      onChange: (val: string) => {
        const clean = removeEmoji(val);
        handleInputChange("email", clean);
        // Reset the "email already exists" error when user types
        if (errorEmailExists) {
          validateEmail(clean, true); // pass a flag to reset the exists error
        } else {
          validateEmail(clean);
        }
      },
    },
    {
      key: "osb",
      label: "Prev. Out. Balance",
      value: formData.osb,
      onChange: (val: string) => handleInputChange("osb", val.replace(/\D/g, "").slice(0, 6)),
    },

    {
      key: "address1",
      label: "Street/Village",
      variant: "standard",
      value: formData.address1,
      onChange: (val: string) => handleInputChange("address1", removeEmoji(val)),
    },
    {
      key: "address2",
      label: "District/State",
      variant: "standard",
      value: formData.address2,
      onChange: (val: string) => handleInputChange("address2", removeEmoji(val)),
    },
    {
      key: "pincode",
      label: "Pincode/Zipcode",
      variant: "standard",
      value: formatPincode(formData.pincode),
      onChange: (val: string) => handleInputChange("pincode", val.replace(/\s/g, "")),
    },
  ];

  // ✅ Check required fields before enabling Add Customer
  const isFormValid = formData.name.trim() !== "" && formData.phone.trim().length === 8 && !emailError && !errorEmailExists;

  return (
    <ModalWrapper
      title='Enter Customer Details'
      useTheme={true}
      onClose={onBack}
      showBackButton
      onBack={onBack}
      width='max-w-2xl'
      className='min-w-[560px]'
      positionClass='top-0 left-0 right-0 mt-16 h-full'
    >
      <div className='grid grid-cols-2 gap-2'>
        {fields
          .filter((f) => !["address1", "address2", "pincode"].includes(f.key))
          .map(({ key, label, type, value, error, helperText, onChange, required }) => (
            <TextField
              key={key}
              label={renderLabel(label, required)}
              type={type}
              value={value}
              error={!!error}
              helperText={helperText}
              onChange={(e) => onChange(e.target.value)}
              style={{ width: "95%", marginTop: "10px" }}
            />
          ))}

        {/* ✅ Address section */}
        <div className='col-span-2 mt-2'>
          <label className='block text-xl font-semibold mb-1'>Address</label>
          <div className='border border-gray-300 rounded-lg p-2 space-y-2'>
            {fields
              .filter((f) => ["address1", "address2", "pincode"].includes(f.key))
              .map(({ key, label, value, onChange, variant }) => (
                <TextField key={key} label={label} variant={variant as any} value={value} onChange={(e) => onChange(e.target.value)} fullWidth />
              ))}
          </div>
        </div>

        <div className='mt-2 flex justify-end gap-4 col-span-2'>
          <CommonButtons className='w-36' label='Cancel' variant='outlineDanger' onClick={onBack} disabled={submitting} />
          <CommonButtons
            className='w-36'
            label='Add Customer'
            variant='primary'
            onClick={handleAddCustomer}
            loading={submitting}
            disabled={!isFormValid || submitting}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default CustomerForm;
